import { useMemo } from 'react';
import { Box, Flex } from '@theme-ui/components';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';

import useMediaQuery from '@app/helpers/useMediaQuery';
import { screenSmall } from '@app/styles/theme/breakpoints';
import { rem } from '@app/styles/theme/layout';

import InfoIcon from '../../assets/icons/InfoIcon';
import { AlertLayout, AlertType } from './Alert.constants';
import { ActionsContainer, Container, getAlertStyles } from './Alert.styles';
import ErrorIcon from './ErrorIcon';
import SuccessIcon from './SuccessIcon';
import WarningIcon from './WarningIcon';

export { AlertType, AlertLayout };

export const Alert = ({
  type = AlertType.Info,
  shouldShake = false,
  layout,
  actions,
  children,
  ...props
}: AlertProps) => {
  const theme = useTheme();

  const isMobile = useMediaQuery(`(max-width: ${screenSmall})`, {
    ssrMatch: true,
  });

  const defaultLayout = isMobile ? AlertLayout.Column : AlertLayout.Row;

  const alertStyles = useMemo(
    () => getAlertStyles(theme, type, shouldShake),
    [type, shouldShake, theme],
  );

  const Icon = useMemo(() => {
    switch (type) {
      case AlertType.Success:
        return SuccessIcon;
      case AlertType.Warning:
        return WarningIcon;
      case AlertType.Error:
        return ErrorIcon;
      default:
        return InfoIcon;
    }
  }, [type]);

  return (
    <Container {...props} role="alert" css={alertStyles}>
      <Icon fill="currentColor" />
      <Flex sx={{ width: '100%', flexDirection: layout }}>
        <Box sx={{ flexGrow: 1, fontSize: rem(14) }}>{children}</Box>
        {actions ? (
          <ActionsContainer layout={layout ?? defaultLayout}>
            {actions}
          </ActionsContainer>
        ) : null}
      </Flex>
    </Container>
  );
};

const contentMargins = css`
  &:not(:last-child) {
    margin-bottom: ${rem(4)};
  }
`;

export const AlertTitle = styled.div`
  font-weight: 500;
  ${contentMargins}
`;

export const AlertMessage = styled.div`
  color: ${({ theme }) => theme.colors.black};
  ${contentMargins}
`;

export const AlertAction = styled.div`
  font-size: ${rem(14)};

  & button {
    font: inherit;
    padding: 0;
    color: currentColor;
    border: none;
    background: none;

    &:not(:disabled) {
      cursor: pointer;
    }
  }

  & a {
    font: inherit;
    text-decoration: underline;

    &:hover {
      text-decoration: none;
    }
  }
`;

type AlertProps = {
  type?: AlertType;
  layout?: AlertLayout;
  shouldShake?: boolean;
  actions?: React.ReactNode;
  children: React.ReactNode;
};
